import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  RouterProvider, createBrowserRouter, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { PostHogProvider } from '@posthog/react';
import type { PostHogInterface } from 'posthog-js';
import { I18nProvider } from 'react-aria-components';
import App from './App';
import './index.css';
import Login from './routes/login';
import { loginAction } from './components/login/loginForm';
import ErrorPage from './pages/errorPage';
import CampaignSummary, { loader as summaryLoader } from './routes/campaignSummary';
import Settings from './routes/settings';
import NewCampaign, { action as createCampaign, loader as snapshotLoader } from './routes/campaignNew';
import CampaignConfirmation, { action as campaignAction } from './routes/campaignConfirmation';
import Campaigns, { loader as campaignsLoader } from './routes/campaigns';
import Support from './routes/support';
import LoadingModal from './components/loadingModal';
import SupportPolicy from './routes/supportPolicy';
import SupportProduct from './routes/supportProduct';
import {
  clientServicesAccess, createCampaignAccess, householdDataAccess,
  wardAdminAccess
} from './utils/authRoleWrap';
import StreetView, { loader as streetViewLoader } from './routes/streetView';
import CampaignReminder, { loader as campaignReminderLoader, action as campaignReminderAction } from './routes/campaignReminder';
import CampaignsLayout from './components/campaigns/campaignsLayout';
import Admin from './routes/admin';
import Home, { loader as homeLoader } from './routes/home';
import Benefits from './routes/benefits';
import Debt, { loader as debtLoader, handle as debtHandle } from './routes/debt';
import DebtInsights, { loader as debtInsightsLoader } from './routes/debtInsights';
import DebtLayout from './components/debt/debtLayout';
import DropBox, { loader as dropBoxLoader } from './routes/dropBox';
import OutcomeTracker, { loader as outcomeLoader } from './routes/outcomeTracker';
import { FeatureFlags } from './types/enums';
// posthog setup directly taken from https://posthog.com/docs/feature-flags/installation/react
const options = {

  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,

  ui_host: import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST,
  defaults: '2026-01-30',
  bootstrap: {
    featureFlags: {
      [FeatureFlags.fileExchangeEnabled]: false,
      [FeatureFlags.hmrcDataToggleEnabled]: false,
    },
  },
  loaded: (posthog: PostHogInterface) => posthog.setPersonPropertiesForFlags({ environment: import.meta.env.MODE }),
} as const;

if (!window.location.hostname.includes('localhost')) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (error instanceof Error && error.message.includes('/backend/auth/*')) {
        return null;
      }
      return event;
    },
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/^https:\/\/lift-2\.co.uk\/*/, /^https:\/\/lift-2-staging\.co.uk\/*/],
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

function RootHydrateFallback() {
  return <LoadingModal message="...loading" />;
}

const AdminSettingsRoute = wardAdminAccess(Settings);
const AdminSupportRoute = clientServicesAccess(SupportPolicy);
const ClientServicesAdminRoute = clientServicesAccess(Admin);
const AdminDropBoxRoute = wardAdminAccess(DropBox);
const NewCampaignRoute = createCampaignAccess(NewCampaign);
const CampaignReminderRoute = createCampaignAccess(CampaignReminder);
const OutcomeTrackerRoute = householdDataAccess(OutcomeTracker);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
const router = sentryCreateBrowserRouter([{
  path: '/',
  element: <App />,
  id: 'root',
  errorElement: <ErrorPage />,
  HydrateFallback: RootHydrateFallback,
  children: [
    {
      index: true,
      element: <Home />,
      loader: homeLoader,
      errorElement: <ErrorPage />,
    }, {
      path: 'settings',
      element: <AdminSettingsRoute />,
      errorElement: <ErrorPage />,
    },
    {
      path: 'admin',
      element: <ClientServicesAdminRoute />,
      errorElement: <ErrorPage />,
    },
    {
      path: 'campaigns',
      element: <CampaignsLayout />,
      errorElement: <ErrorPage />,
      children: [{
        index: true,
        element: <Campaigns />,
        loader: campaignsLoader,
        errorElement: <ErrorPage />,
      }, {
        path: 'new',
        element: <NewCampaignRoute />,
        action: createCampaign,
        loader: snapshotLoader,
        errorElement: <ErrorPage />,
      }, {
        path: 'new/:campaignId',
        action: campaignAction,
        element: <CampaignConfirmation />,
        errorElement: <ErrorPage />,
      }, {
        path: ':id/reminder',
        loader: campaignReminderLoader,
        action: campaignReminderAction,
        element: <CampaignReminderRoute />,
        errorElement: <ErrorPage />,
      }, {
        path: ':id',
        element: <CampaignSummary />,
        loader: summaryLoader,
        errorElement: <ErrorPage />,
      }],
    },
    {
      path: ':impactTheme/new',
      element: <NewCampaign />,
      action: createCampaign,
      loader: snapshotLoader,
      errorElement: <ErrorPage />,
    }, {
      path: ':impactTheme/new/:campaignId',
      action: campaignAction,
      element: <CampaignConfirmation />,
      errorElement: <ErrorPage />,
    },
    {
      path: 'streetview',
      element: <StreetView />,
      loader: streetViewLoader,
      errorElement: <ErrorPage />,
    }, {
      path: 'benefit-take-up',
      element: <Benefits />,
      // loader: benefitsLoader,
      errorElement: <ErrorPage />,
    }, {
      path: 'debt',
      element: <DebtLayout />,
      errorElement: <ErrorPage />,
      handle: debtHandle,
      children: [{
        index: true,
        element: <Debt />,
        loader: debtLoader,
        errorElement: <ErrorPage />,
      }, {
        path: ':debtType',
        element: <DebtInsights />,
        loader: debtInsightsLoader,
        errorElement: <ErrorPage />,
      }],
    }, {
      path: 'file-exchange',
      element: <AdminDropBoxRoute />,
      loader: dropBoxLoader,
      errorElement: <ErrorPage />,
    }, {
      path: '/support',
      element: <Support />,
      errorElement: <ErrorPage />,
    },
    {
      path: '/support/policy',
      element: <AdminSupportRoute />,
      errorElement: <ErrorPage />,
    }, {
      path: '/support/product',
      element: <SupportProduct />,
      errorElement: <ErrorPage />,
    }, {
      path: '/outcome-tracker',
      element: <OutcomeTrackerRoute />,
      loader: outcomeLoader,
      errorElement: <ErrorPage />,
    }],
}, {
  path: 'login',
  element: <Login />,
  action: loginAction,
  errorElement: <ErrorPage />,
}]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorPage />} showDialog>
      <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
        <I18nProvider locale="en-GB">
          <RouterProvider router={router} />
        </I18nProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
